'use client';

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import AppImage from '@/components/ui/AppImage';
import { useTranslation } from 'react-i18next';

export default function TrustSection() {
  const { t } = useTranslation('home');
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const trustBadges = [
    {
      icon: '🧪',
      title: t('trust.badges.labTested.title'),
      description: t('trust.badges.labTested.description'),
      bg: 'bg-primary-50',
      border: 'border-primary-100'
    },
    {
      icon: '🌿',
      title: t('trust.badges.natural.title'),
      description: t('trust.badges.natural.description'),
      bg: 'bg-surface',
      border: 'border-border'
    },
    {
      icon: '👨‍👩‍👧',
      title: t('trust.badges.familySafe.title'),
      description: t('trust.badges.familySafe.description'),
      bg: 'bg-blue-pale',
      border: 'border-blue-brand/20'
    },
    {
      icon: '🏆',
      title: t('trust.badges.qualityCertified.title'),
      description: t('trust.badges.qualityCertified.description'),
      bg: 'bg-amber-50',
      border: 'border-amber-100'
    }
  ];

  const testimonials = [
    {
      name: t('trust.testimonials.priya.name'),
      location: t('trust.testimonials.priya.location'),
      role: t('trust.testimonials.priya.role'),
      quote: t('trust.testimonials.priya.quote'),
      rating: 5,
      avatar: '/assets/images/only_bottel.png',
      avatarAlt: t('trust.testimonials.avatarAlt')
    },
    {
      name: t('trust.testimonials.rajesh.name'),
      location: t('trust.testimonials.rajesh.location'),
      role: t('trust.testimonials.rajesh.role'),
      quote: t('trust.testimonials.rajesh.quote'),
      rating: 5,
      avatar: '/assets/images/only_bottel.png',
      avatarAlt: t('trust.testimonials.avatarAlt')
    },
    {
      name: t('trust.testimonials.ananya.name'),
      location: t('trust.testimonials.ananya.location'),
      role: t('trust.testimonials.ananya.role'),
      quote: t('trust.testimonials.ananya.quote'),
      rating: 5,
      avatar: '/assets/images/only_bottel.png',
      avatarAlt: t('trust.testimonials.avatarAlt')
    }
  ];

  return (
    <section
      id="trust"
      ref={ref}
      className="py-32 lg:py-40 bg-white"
      aria-labelledby="trust-heading">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}>
          
          <span className="text-xs font-semibold tracking-widest uppercase text-muted mb-4 block">
            {t('trust.label')}
          </span>
          <h2
            id="trust-heading"
            className="section-headline font-display font-light text-foreground">
            
            {t('trust.title.line1')}
            <br />
            <span className="italic pl-0.5 pr-2 text-gradient-green">{t('trust.title.line2')}</span>
          </h2>
        </motion.div>

        {/* Trust badges — asymmetric */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
          {trustBadges?.map((badge, i) =>
          <motion.div
            key={badge?.title}
            initial={{ opacity: 0, y: 40, rotate: i % 2 === 0 ? -1 : 1 }}
            animate={inView ? { opacity: 1, y: 0, rotate: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
            className={`${badge?.bg} border ${badge?.border} rounded-4xl p-8 trust-badge text-center`}>
            
              <span className="text-5xl mb-5 block">{badge?.icon}</span>
              <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                {badge?.title}
              </h3>
              <p className="text-muted text-sm leading-relaxed">{badge?.description}</p>
            </motion.div>
          )}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials?.map((t, i) =>
          <motion.div
            key={t?.name}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
            className={`bg-surface border border-border rounded-4xl p-8 trust-badge ${i === 1 ? 'lg:mt-8' : ''}`}>
            
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {Array.from({ length: t?.rating })?.map((_, si) =>
              <span key={si} className="text-green-800 text-lg">★</span>
              )}
              </div>

              <blockquote className="font-display italic text-xl font-light text-foreground leading-relaxed mb-8">
                "{t?.quote}"
              </blockquote>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary-100 shrink-0">
                  <AppImage
                  src={t?.avatar}
                  alt={t?.avatarAlt}
                  width={48}
                  height={48}
                  className="w-full h-full object-contain bg-white p-1" />
                
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm">{t?.name}</p>
                  <p className="text-muted text-xs">
                    {t?.role} · {t?.location}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>);


}